// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { fontConfig } from "@bg/XDL/PreStyle/typography";
import { getFocalPointStyles, parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { responsiveStyleRules } from "@insite/client-framework/Common/Utilities/responsive";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { LinkPresentationProps } from "@insite/mobius/Link";
import Page from "@insite/mobius/Page";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import debounce from "lodash/debounce";
import React, { FC, useState } from "react";
import { css } from "styled-components";

const enum fields {
    textAlignment = "textAlignment",
    specificHeight = "specificHeight",
    background = "background",
    image = "image",
    focalPoint = "focalPoint",
    backgroundColor = "backgroundColor",
    heading = "heading",
    minimumHeight = "minimumHeight",
    subheading = "subheading",
    buttonLabel = "buttonLabel",
    buttonLink = "buttonLink",
    buttonVariant = "variant",
    bannerWidth = "bannerWidth",
    imageOverlay = "imageOverlay",
    disableButton = "disableButton",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.textAlignment]: "right" | "left";
        [fields.specificHeight]: number;
        [fields.background]: "image" | "color";
        [fields.image]: string;
        [fields.focalPoint]:
            | "topLeft"
            | "topCenter"
            | "topRight"
            | "centerLeft"
            | "center"
            | "centerRight"
            | "bottomLeft"
            | "bottomCenter"
            | "bottomRight";
        [fields.backgroundColor]: string;
        [fields.heading]: string;
        [fields.minimumHeight]: "1/4 viewport" | "1/2 viewport" | "3/4 viewport" | "fullViewport";
        [fields.subheading]: string;
        [fields.buttonLabel]: string;
        [fields.buttonLink]: LinkFieldValue;
        [fields.buttonVariant]: "primary" | "secondary" | "tertiary";
        [fields.imageOverlay]: string;
        [fields.disableButton]: boolean;
    };
    extendedStyles?: BannerStyles;
}

export interface BannerStyles {
    wrapper?: InjectableCss;
    overlayWrapper?: InjectableCss;
    slideContentContainer?: InjectableCss;
    slideCenteringWrapperStyles?: InjectableCss;
    heading?: TypographyPresentationProps;
    subText?: TypographyPresentationProps;
    bannerButton?: ButtonPresentationProps;
}

export const bannerStyles: BannerStyles = {
    wrapper: {
        css: css`
            width: 100%;
            @media (max-width: 768px) {
                background-size: cover;
            }
        `,
    },
    overlayWrapper: {
        css: css`
            width: 100%;
        `,
    },
    slideContentContainer: {
        css: css`
            display: flex;
            align-items: center;
        `,
    },
    slideCenteringWrapperStyles: {
        css: css`
            height: fit-content;
            max-width: 550px;
            background: white;
            padding: 40px;
            box-shadow: 0 2px 11px 0 rgb(53, 65, 94, 0.25);
            @media (max-width: 600px) {
                padding: 20px;
                max-width: 100%;
                background: rgba(255, 255, 255, 0.8);
            }
        `,
    },
    heading: {
        variant: "h1",
    },
    subText: {
        css: css`
            margin-bottom: 30px;
            display: block;
        `,
    },
    bannerButton: {
        css: css`
            &&& > * {
                font-weight: ${fontConfig.weight.bold};
            }
        `,
    },
};

export const Banner: FC<OwnProps> = ({ fields, extendedStyles }) => {
    const { url, title } = useGetLink(fields.buttonLink);
    const history = useHistory();
    const onClick = () => {
        if (url) {
            history.push(url);
        }
    };
    const [secondWidth, setSecondWidth] = useState(-10000);

    React.useEffect(() => {
        const updateSize = debounce(() => {
            const width = (window.innerWidth - 1360) / 2 + 443;
            setSecondWidth(width);
        }, 150);
        window.addEventListener("resize", updateSize);
        updateSize();
        return () => window.removeEventListener("resize", updateSize);
    }, []);

    const focalPointStyles = getFocalPointStyles(fields.focalPoint);

    let minimumHeightStyles;
    if (!fields.specificHeight) {
        switch (fields.minimumHeight) {
            case "1/4 viewport":
                minimumHeightStyles = "min-height: 25vh;";
                break;
            case "1/2 viewport":
                minimumHeightStyles = "min-height: 50vh;";
                break;
            case "3/4 viewport":
                minimumHeightStyles = "min-height: 75vh;";
                break;
            case "fullViewport":
                minimumHeightStyles = "min-height: 100vh;";
                break;
        }
    } else {
        minimumHeightStyles = `min-height: ${fields.specificHeight}px;`;
    }

    const styles = useMergeStyles("banner", bannerStyles, extendedStyles);
    const overlayWrapperStyles = `       
            background-image: url(${fields.image});
            background-size: calc(100% - ${secondWidth}px) 100%;
            background-repeat: no-repeat;
            ${styles.overlayWrapper?.css || ""}
        `;

    const wrapperStyles = {
        css: css`
            ${styles.wrapper?.css || ""}
            ${focalPointStyles}
            ${minimumHeightStyles}
            ${overlayWrapperStyles}            
            background-position: ${fields.textAlignment === "right" ? "left" : "right"};
            background-color: ${getColor("common.ui4")};
        `,
    };

    const contentWrapperStyles = {
        css: css`
            ${minimumHeightStyles}
            ${styles.slideContentContainer?.css || ""}
            justify-content: ${fields.textAlignment === "right" ? "flex-end" : "flex-start"};
            @media (max-width: 600px) {
                padding: 0;
                align-items: flex-end;
            }
        `,
    };
    return (
        <StyledWrapper {...wrapperStyles}>
            <Page {...contentWrapperStyles}>
                <StyledWrapper {...styles.slideCenteringWrapperStyles}>
                    <Typography {...styles.heading}>{parse(fields.heading, parserOptions)}</Typography>
                    <Typography {...styles.subText}>{parse(fields.subheading, parserOptions)}</Typography>
                    {!fields.disableButton && (
                        <Button {...styles.bannerButton} variant={fields.variant} onClick={onClick}>
                            {fields.buttonLabel || title || url}
                        </Button>
                    )}
                </StyledWrapper>
            </Page>
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const settingsTab = {
    displayName: "Settings",
    sortOrder: 1,
};

const banner: WidgetModule = {
    component: Banner,
    definition: {
        group: "XNGAGE" as any,
        icon: "Banner",
        fieldDefinitions: [
            {
                fieldType: "General",
                name: fields.background,
                displayName: "Background",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Image", value: "image" },
                    { displayName: "Color", value: "color" },
                ],
                defaultValue: "image",
                hideEmptyOption: true,
                tab: basicTab,
            },
            {
                fieldType: "Translatable",
                name: fields.image,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                isVisible: widget => widget.fields.background === "image",
                tab: basicTab,
            },
            {
                fieldType: "General",
                name: fields.imageOverlay,
                displayName: "Image Color Overlay",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                isVisible: widget => widget.fields.background === "image",
                tab: basicTab,
            },
            {
                fieldType: "General",
                name: fields.focalPoint,
                displayName: "Focal Point",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Top Left", value: "topLeft" },
                    { displayName: "Top Center", value: "topCenter" },
                    { displayName: "Top Right", value: "topRight" },
                    { displayName: "Center Left", value: "centerLeft" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Center Right", value: "centerRight" },
                    { displayName: "Bottom Left", value: "bottomLeft" },
                    { displayName: "Bottom Center", value: "bottomCenter" },
                    { displayName: "Bottom Right", value: "bottomRight" },
                ],
                defaultValue: "center",
                hideEmptyOption: true,
                isVisible: widget => widget.fields.background === "image",
                tab: basicTab,
            },
            {
                fieldType: "General",
                name: fields.backgroundColor,
                editorTemplate: "ColorPickerField",
                displayName: "Color",
                defaultValue: "black",
                isVisible: widget => widget.fields.background === "color",
                tab: basicTab,
            },
            {
                fieldType: "Translatable",
                name: fields.heading,
                editorTemplate: "RichTextField",
                displayName: "Heading",
                defaultValue: "",
                extendedConfig: { height: 100 },
                expandedToolbarButtons: {
                    moreMisc: {},
                    code: {},
                },
                tab: basicTab,
            },
            {
                fieldType: "Translatable",
                name: fields.subheading,
                editorTemplate: "RichTextField",
                displayName: "Subheading",
                defaultValue: "",
                extendedConfig: { height: 170 },
                expandedToolbarButtons: {
                    moreMisc: {},
                    code: {},
                },
                tab: basicTab,
            },
            {
                fieldType: "Translatable",
                name: fields.buttonLabel,
                editorTemplate: "TextField",
                displayName: "Button Label",
                defaultValue: "",
                tab: basicTab,
            },
            {
                fieldType: "General",
                name: fields.buttonLink,
                displayName: "Button Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                tab: basicTab,
            },
            {
                name: fields.buttonVariant,
                displayName: "Button Variant",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Primary", value: "primary" },
                    { displayName: "Secondary", value: "secondary" },
                    { displayName: "Tertiary", value: "tertiary" },
                ],
                hideEmptyOption: true,
                defaultValue: "primary",
                fieldType: "General",
                tab: basicTab,
            },
            {
                fieldType: "General",
                name: fields.disableButton,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                tab: basicTab,
            },
            {
                fieldType: "General",
                name: fields.minimumHeight,
                displayName: "Minimum Banner Height",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "1/4 Viewport", value: "1/4 viewport" },
                    { displayName: "1/2 Viewport", value: "1/2 viewport" },
                    { displayName: "3/4 Viewport", value: "3/4 viewport" },
                    { displayName: "Full Viewport", value: "fullViewport" },
                ],
                defaultValue: "1/4 viewport",
                hideEmptyOption: true,
                tab: settingsTab,
            },
            {
                name: fields.specificHeight,
                displayName: "Specific Height",
                fieldType: "General",
                editorTemplate: "IntegerField",
                defaultValue: 0,
                tab: settingsTab,
            },
            {
                name: fields.textAlignment,
                editorTemplate: "DropDownField",
                defaultValue: "center",
                fieldType: "General",
                options: [
                    { displayName: "Left", value: "left" },
                    { displayName: "Right", value: "right" },
                ],
                hideEmptyOption: true,
                tab: settingsTab,
            },
        ],
    },
};

export default banner;
